import { Button, Icon, IconButton, InputStepper, Text, Stack, VStack } from '@genial/components';
import { Utils, useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React, { useCallback } from 'react';

import { PrivatePensionAssetController } from '../../../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { PrivatePensionBeneficiary } from '../../../../../../../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension/types';

export namespace BeneficiaryItemTypes {
  export interface Props {
    beneficiary: PrivatePensionBeneficiary;
    index: number;
    onEdit: (index: number) => void;
    onRemove: (index: number) => void;
    offerController: PrivatePensionAssetController;
  }
}

export const BeneficiaryItem: React.FC<BeneficiaryItemTypes.Props> = ({
  beneficiary,
  index,
  onEdit,
  onRemove,
  offerController,
}) => {
  const { relatedPartyOptions, accountHolderForm } = offerController;
  const {
    formState: { errors },
  } = accountHolderForm;
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const relatedPartyName = relatedPartyOptions.find(
    (option) => Number(option.value) === beneficiary.relatedPartyId
  )?.label;

  const handleParticipationPercentageChange = useCallback(
    (value: number) => {
      offerController.updateBeneficiary(index, {
        ...beneficiary,
        participationPercentage: value,
      });
    },
    [index, offerController, beneficiary]
  );

  return (
    <Stack
      direction={{ base: 'column', sm: 'row' }}
      space={{ base: 'stack.xxxs', sm: 'stack.nano' }}>
      <Stack direction={{ base: 'row', sm: 'column' }} flex={1}>
        <Text variant="semibold" flex={1}>
          Nome completo
        </Text>
        <Text flex={1} textAlign={{ base: 'right', sm: 'left' }}>
          {beneficiary.name}
        </Text>
      </Stack>
      <Stack direction={{ base: 'row', sm: 'column' }} flex={1}>
        <Text variant="semibold" flex={1}>
          Data de nascimento
        </Text>
        <Text flex={1} textAlign={{ base: 'right', sm: 'left' }}>
          {Utils.Mask.DateFormat(beneficiary.birthDate, 'dd/MM/yyyy')}
        </Text>
      </Stack>
      <Stack direction={{ base: 'row', sm: 'column' }} flex={1}>
        <Text variant="semibold" flex={1}>
          Parentesco
        </Text>
        <Text flex={1} textAlign={{ base: 'right', sm: 'left' }}>
          {relatedPartyName}
        </Text>
      </Stack>
      <VStack>
        <Text variant="semibold">Participação</Text>
        <InputStepper
          stepperValue={1}
          min={1}
          max={100}
          name="participationPercentage"
          onChange={handleParticipationPercentageChange}
          defaultValue={beneficiary.participationPercentage}
          customError={errors.beneficiariesPercentage?.message ? ' ' : undefined}
        />
      </VStack>
      {isMobile ? (
        <Box flex={1} justifyContent="space-between" flexDirection="row">
          <Button
            variant="textLink"
            onPress={() => onEdit(index)}
            _text={{ color: 'brand.tertiary.principal' }}
            leftIcon={<Icon name="Edit" size="xs" color="brand.tertiary.principal" />}>
            EDITAR
          </Button>
          <Button
            variant="textLink"
            onPress={() => onRemove(index)}
            _text={{ color: 'feedback.error.default' }}
            leftIcon={<Icon name="Trash" size="xs" color="feedback.error.default" />}>
            REMOVER
          </Button>
        </Box>
      ) : (
        <>
          <IconButton
            testId="portability-item-edit-button"
            onPress={() => onEdit(index)}
            icon="Edit"
            iconSize="xs"
            iconColor="brand.tertiary.principal"
          />
          <IconButton
            icon="Trash"
            onPress={handleRemove}
            testId="remove-beneficiary"
            iconSize="xs"
            iconColor="feedback.error.default"
          />
        </>
      )}
    </Stack>
  );
};
